import {
  AfterViewInit,
  Component,
  ComponentRef,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { PersonService } from '../../../services/person.service';
import { SettingsService } from '../../../services/settings.service';
import { Person } from '../../../models/person';
import interact from 'interactjs';
import { InteractionService } from '../../../services/interaction.service';
import { SynchronizationService } from '../../../services/synchronization.service';
import { Subscription } from 'rxjs';
import { Project } from '../../../models/project';
import { AwallComponentEvent } from '../../../models/awallComponentEvent';
import { HTMLElementPositioningHelper } from '../../../util/htmlelement-positioning-helper';
import { HighlightTeammemberService } from '../../../services/highlight-teammember.service';
import { IWidget } from '../../../interfaces/iWidget';
import { SettingsEventType } from '../../../models/enums/settingsEventType';
import { IssueComponent } from '../../issue/issue.component';
import { ComponentEvent } from '../../../models/enums/componentEvent';
import { Listener, CommunicationService } from '../../../services/communication.service';
import { ProjectService } from '../../../services/project.service';
import { TeamMoodDataCollectionComponent } from '../team-mood/team-mood-data-collection/team-mood-data-collection.component';

@Component({
  selector: 'app-team-widget',
  templateUrl: './team-widget.component.html',
  styleUrls: ['./team-widget.component.scss']
  })

export class TeamWidgetComponent implements IWidget, OnInit, AfterViewInit, OnDestroy, Listener<AwallComponentEvent>  {
  @ViewChild('teamContainer') teamContainer: ElementRef;
  public static readonly componentId = 'TeamWidgetComponent';
  public static widgetName = 'team';
  private pictureOffset = 24;
  public project: Project;
  public team: Person[] = [];
  public person: Person;
  private interactedClass = 'resizedwidget';
  private subscriptions: Subscription[] = [];
  private element: ElementRef;

  // Initializes the viewport sizes
  private screenHeight;
  private screenWidth;

  // style
  public zIndex = 25;

  // ref
  public componentRef: ComponentRef<any>;
  public isUndocked: boolean;

  private whRatio = 90.8 / 57.5; // width-height ratio
  @HostListener('window:resize', ['$event'])
  onResize(event?) {
    this.screenHeight = window.innerHeight;
    this.screenWidth = window.innerWidth;
  }

  constructor(
    private projectService: ProjectService,
    private personService: PersonService,
    private settings: SettingsService,
    private communicationService: CommunicationService,
    private interactionService: InteractionService,
    private synchronization: SynchronizationService,
    private positionHelper: HTMLElementPositioningHelper,
    private highlightService: HighlightTeammemberService
  ) { }

  ngOnInit() {
    this.communicationService.registerComponent(TeamWidgetComponent.componentId, this);
    this.initSettingsSubscription();
    this.updateData();
  }

  ngAfterViewInit() {
    this.element = this.teamContainer;
    this.setupTouchInteractions();
    this.setupRealtimeEvents();
  }

  ngOnDestroy(): void {
    for (const ev of this.subscriptions) {
      ev.unsubscribe();
    }
    this.communicationService.unregisterComponent(TeamWidgetComponent.componentId);

    // Cleanup interactjs-listeners
    this.interactionService.unset('.team-widget');
    this.interactionService.unset('.draggableteamwidget');
    this.interactionService.unset('.teamwidgetdropzone');
  }

  public getName() {
    return TeamWidgetComponent.widgetName;
  }

  public setUndocked(isUndocked: boolean) {
    this.isUndocked = isUndocked;
  }

  /**
   * Subscribing to the settings-Observable to get notified when project/sprint changes
   */
  private initSettingsSubscription() {
    this.subscriptions.push(this.settings.getUpdateObservable().subscribe(settingsEventType => {
      if (settingsEventType === SettingsEventType.PROJECT_CHANGED) {
        this.updateData();
      }
    }));
  }

  private updateData() {
    this.loadSelectedProject();
    this.loadTeamData();
  }

  /**
   * Load and set project on initialization.
   * If project is set or changed in project widget method is called by setting service update observable to update project.
   */
  loadSelectedProject() {
    const projectName = this.settings.getProjectName();
    if (projectName) {
      this.projectService.getProjectByKey(projectName).subscribe(project => {
        if (project !== null) {
          this.project = project;
        }
      });
    }
  }

  private loadTeamData() {
    this.team = [];
    const projectName = this.settings.getProjectName();
    if (projectName) {
      this.personService.getMembers(projectName).subscribe(members => {
        this.team = members;
      });
    }
  }

  private setupTouchInteractions() {
    this.setupAvatarDropzone();
    this.setupAvatarDragInteraction();
    this.setupAvatarTapInteraction();
  }

  private setupAvatarDropzone() {
    const localThis = this;
    interact('.teamwidgetdropzone').dropzone({
      // only accept elements matching this CSS selector
      accept: '.draggableteamwidget',
      // Require a 50% element overlap for a drop to be possible
      overlap: 0.50,
      // listen for drop related events:
      ondragenter: function (event: any) {
        event.target.classList.add('droptarget');
      },
      ondragleave: function (event: any) {
        event.target.classList.remove('droptarget');
      },
      ondrop: function (event: any) {
        event.target.classList.remove('droptarget');
        const memberID = event.relatedTarget.getAttribute('data-member-id');
        const person = localThis.team.filter(value => value.id === memberID)[0];
        const issueId = event.target.getAttribute('data-issueid');
        const issueProjectKey = event.target.getAttribute('data-projectkey');

        if (issueId && issueProjectKey) {
          // Send Event to the issuecomponent informing it of the drop
          const isReporter = event.target.classList.contains('reporter');
          const eventPayload = { 'issueId': issueId, 'person': person, 'isReporter': isReporter };
          const componentEvent = new AwallComponentEvent(ComponentEvent.ISSUE_TEAMMEMBER_DROPPED, eventPayload);
          const targetComponentId = IssueComponent.getIssueComponentId(issueProjectKey, issueId);

          localThis.communicationService.sendData(targetComponentId, componentEvent);
        } else if (event.target.hasAttribute('data-voting-id')) {
          localThis.communicationService.sendData(TeamMoodDataCollectionComponent.componentId, person);
        } else {
          console.log('Cant send event for dropped teammember. No component-id found on target: ', event.target);
        }
      },
    });
  }

  private setupAvatarDragInteraction() {
    const localThis = this;
    const widgetPosition: any = { top: null, left: null };
    interact('.draggableteamwidget').draggable({
      onstart: function (event) {
        const draggedElement: HTMLElement = event.target;

        // create a clone of the dragged teammember here that takes the place of the dragged-out one
        const clone = draggedElement.cloneNode(true);
        draggedElement.parentElement.insertBefore(clone, draggedElement);

        const widgetElement: any = draggedElement.closest('#team-widget').getBoundingClientRect();
        widgetPosition.top = widgetElement.top;
        widgetPosition.left = widgetElement.left;

        draggedElement.style.position = 'absolute';
      },
      // call this function on every dragmove event
      onmove: function (event) {
        const draggedElement: HTMLElement = event.target;
        draggedElement.style.top = (event.clientY - localThis.pictureOffset - widgetPosition.top) + 'px';
        draggedElement.style.left = (event.clientX - localThis.pictureOffset - widgetPosition.left) + 'px';
      },
      onend: function (event) {
        // Remove the dragged out teammember here. It is not needed anymore after the dragging ends, because
        // it has been replaced with a cloned element onStart of dragging
        const target: HTMLElement = event.target;
        if (target.parentElement) {
          target.parentElement.removeChild(target);
        }
      }
      // This is important so drag doesnt end when dragging over input fields etc.
    }).preventDefault('always');
  }

  private setupAvatarTapInteraction(): void {
    const localThis = this;
    interact('.draggableteamwidget').on('tap', function (event) {
      localThis.handleAvatarTapped(event);
    });
  }

  /* Toggle highlighting of issues on tap-gesture on a avatar-picture */
  private handleAvatarTapped(event) {
    const person = this.team.find(value => value.id === event.currentTarget.getAttribute('data-member-id'));
    this.highlightService.toggleHighlighting(person);
  }

  /**
   * Set up the event listeners for real-time synchronization.
   */
  private setupRealtimeEvents() {
    const obj = this;
    this.subscriptions.push(this.synchronization.subscribeEvent<any>(TeamWidgetComponent.widgetName + 'ResizeWidget')
      .subscribe(size => {
        const target = this.element.nativeElement;
        target.style.width = `${size.width}px`;
        target.style.height = `${size.height}px`;
      })
    );

    this.subscriptions.push(this.synchronization.subscribeEvent<any>(TeamWidgetComponent.widgetName + 'StartMoveWidget')
      .subscribe(() => {
        console.log('Got the move');
        const target = this.element.nativeElement;
        target.classList.add(this.interactedClass);
      })
    );

    this.subscriptions.push(this.synchronization.subscribeEvent<any>(TeamWidgetComponent.widgetName + 'EndMoveWidget')
      .subscribe(() => {
        if (!this.isUndocked) {
          this.isUndocked = true;
          const target = this.element.nativeElement;
          // TODO move css-modification to scss file
          target.style.width = '25vh';
          target.style.height = 'auto';
          this.isUndocked = true;
        }
      })
    );

    this.subscriptions.push(this.synchronization.subscribeEvent<any>(TeamWidgetComponent.widgetName + 'MoveWidget')
      .subscribe(positionRelative => {
        const target = this.element.nativeElement;
        const scale = (parseFloat(target.getAttribute('data-scale')) || 1);
        this.positionHelper.setRelativePosition(target, positionRelative.left, positionRelative.top, null, scale);
      })
    );

    this.subscriptions.push(this.synchronization.subscribeEvent<any>(TeamWidgetComponent.widgetName + 'ResetWidget')
      .subscribe(() => {
        this.resetWidget(obj.element.nativeElement);
      })
    );
  }

  private resetWidget(target: HTMLElement) {
    this.isUndocked = false;
    this.interactionService.undoDragInteraction(target);
    this.interactionService.undoPinchToResizeInteraction(target);
    target.classList.remove('widget--is-undocked');
  }

  onCommunicationEvent(event: AwallComponentEvent): void {
    if (!event) { return; }
    switch (event.name) {
      case ComponentEvent.WIDGET_TEAM_HIGHLIGHT_CHANGED: {
        this.updateHighLightColors();
        break;
      }
    }
  }

  public updateHighLightColors() {
    this.team.forEach(member => {
      const highlightColor = this.highlightService.getHighlightColorName(member);
      const selector = 'img[data-member-id=\'' + member.id + '\']';
      const target: HTMLElement = this.element.nativeElement.querySelector(selector);
      target.setAttribute('data-highlightColor', highlightColor);
    });
  }

  setPosition(x: number, y: number) {
    this.element.nativeElement.style.left = x + 'px';
    this.element.nativeElement.style.top = y + 'px';
  }
}
